"use client"

import * as React from "react"
import { Input } from '@/components/ui/input'
import { Label } from "@/components/ui/label"
import { log } from '@/lib/logger'
import { Instagram, CheckCircle2, XCircle, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { INSTAGRAM_CONFIG_ID, INSTAGRAM_APP_ID, INSTAGRAM_REDIRECT_URI, buildInstagramAuthorizeUrl, buildMetaGraphUrl, getConversationsAuthToken } from "@/config/env"
import { instagramService, InstagramServiceError } from "@/services/instagramService"
import type { InstagramConnectionStatus } from "@/services/instagramService"
import { facebookOAuthService, FacebookOAuthError, INSTAGRAM_LOGIN_SCOPES } from "@/services/facebookOAuthService"
import { instagramOAuthService, InstagramOAuthError } from "@/services/instagramOAuthService"
import { notifyChannelConnectionChanged } from "@/lib/channel-connection-events"

interface InstagramConnectionProps {
  translations?: Record<string, unknown>
}

type MetaInstagramAccountRef = {
  id?: string
} | string | null | undefined

type MetaPageAccount = {
  id: string
  name: string
  access_token?: string
  instagram_business_account?: MetaInstagramAccountRef
  connected_instagram_account?: MetaInstagramAccountRef
  connected_page_backed_instagram_account?: MetaInstagramAccountRef
}

type ResolvedInstagramAccount = {
  page_id: string
  page_name: string
  page_access_token: string
  instagram_business_account: { id: string }
}

const INSTAGRAM_PAGE_LINK_FIELDS = [
  'instagram_business_account',
  'connected_instagram_account',
  'connected_page_backed_instagram_account',
].join(',')

const resolveInstagramAccountId = (page: MetaPageAccount): string | null => {
  const candidates = [
    page.instagram_business_account,
    page.connected_instagram_account,
    page.connected_page_backed_instagram_account,
  ]

  for (const candidate of candidates) {
    if (!candidate) {
      continue
    }

    if (typeof candidate === 'string') {
      return candidate
    }

    if (typeof candidate === 'object' && candidate.id) {
      return candidate.id
    }
  }

  return null
}

const fetchPageInstagramAccountId = async (pageId: string, accessToken: string): Promise<string | null> => {
  const response = await fetch(
    buildMetaGraphUrl(
      `${pageId}?access_token=${accessToken}&fields=${INSTAGRAM_PAGE_LINK_FIELDS}`
    )
  )

  const data = await response.json()

  if (data?.error) {
    log.warn('Failed to fetch Instagram business account for page', {
      pageId,
      error: data.error.message,
    })
    return null
  }

  return resolveInstagramAccountId(data as MetaPageAccount)
}

export function InstagramConnection({ translations }: InstagramConnectionProps) {
  const { t } = useTranslation()

  const [connectionStatus, setConnectionStatus] = React.useState<InstagramConnectionStatus | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [initialLoading, setInitialLoading] = React.useState(true)
  const [facebookSdkReady, setFacebookSdkReady] = React.useState(() => facebookOAuthService.isInitialized())
  const [facebookSdkError, setFacebookSdkError] = React.useState<string | null>(null)

  const [manualMode, setManualMode] = React.useState(false)
  const [manualToken, setManualToken] = React.useState("")
  const [fetchedAccounts, setFetchedAccounts] = React.useState<ResolvedInstagramAccount[]>([])
  const [selectedAccount, setSelectedAccount] = React.useState<string>("")

  const getTranslation = React.useCallback((key: string): string => {
    if (translations) {
      const keys = key.split('.')
      let value: Record<string, unknown> = translations
      for (const k of keys) {
        value = value?.[k] as Record<string, unknown>
      }
      return typeof value === 'string' ? value : key
    }
    return t(key)
  }, [translations, t])

  const translateOrFallback = React.useCallback((key: string, fallback: string): string => {
    const translated = getTranslation(key)
    return translated && translated !== key ? translated : fallback
  }, [getTranslation])

  const ensureChannelsTabInUrl = React.useCallback((): string => {
    const nextUrl = new URL(window.location.href)
    nextUrl.searchParams.set('tab', 'channels')
    window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`)
    return `${nextUrl.origin}${nextUrl.pathname}${nextUrl.search}`
  }, [])

  /**
   * The redirect URI Instagram checks against its allow-list. Configurable
   * because Meta's panel is fussy about what it accepts there, and it has to
   * match byte for byte both when asking for the code and when exchanging it.
   */
  const instagramRedirectUri = React.useCallback((): string => {
    if (INSTAGRAM_REDIRECT_URI) {
      // Still normalise the tab so the user lands back on the right screen.
      ensureChannelsTabInUrl()
      return INSTAGRAM_REDIRECT_URI
    }
    return ensureChannelsTabInUrl()
  }, [ensureChannelsTabInUrl])

  const consumeOAuthRedirectParams = React.useCallback((): {
    accessToken?: string
    code?: string
    errorMessage?: string
  } => {
    if (typeof window === 'undefined') {
      return {}
    }

    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash

    const hashParams = new URLSearchParams(hash)
    const searchParams = new URLSearchParams(window.location.search)

    const accessToken = hashParams.get('access_token') || searchParams.get('access_token') || undefined
    // Instagram Login returns an authorization code on the query string instead
    // of an implicit token.
    const code = searchParams.get('code') || undefined
    const errorReason = hashParams.get('error_reason') || searchParams.get('error_reason')
    const errorDescription = hashParams.get('error_description') || searchParams.get('error_description')

    const hasOAuthPayload = accessToken || code || errorReason || hashParams.get('signed_request') || searchParams.get('signed_request')

    if (!hasOAuthPayload) {
      return {}
    }

    const nextUrl = new URL(window.location.href)
    nextUrl.hash = ''
    ;[
      'access_token',
      'code',
      'data_access_expiration_time',
      'expires_in',
      'error',
      'error_code',
      'error_description',
      'error_reason',
      'granted_scopes',
      'graph_domain',
      'signed_request',
      'state',
    ].forEach((key) => nextUrl.searchParams.delete(key))

    window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`)

    if (code) {
      return { code }
    }

    if (accessToken) {
      return { accessToken }
    }

    return {
      errorMessage: errorDescription || errorReason || 'Instagram authorization was not completed.',
    }
  }, [])

  const connectWithInstagramLogin = React.useCallback(async (code: string) => {
    // Instagram validates the redirect_uri against the one used to obtain the
    // code, so it has to be rebuilt identically here.
    const redirectUri = instagramRedirectUri()
    await instagramService.connectWithInstagramLogin(code, redirectUri)

    const status = await instagramService.getStatus()
    setConnectionStatus(status)
    notifyChannelConnectionChanged({ channel: 'instagram', connected: true })
  }, [instagramRedirectUri])

  const connectAccount = React.useCallback(async (
    account: ResolvedInstagramAccount,
    userAccessToken?: string,
  ) => {
    const redirectUri = ensureChannelsTabInUrl()

    const connectData = {
      page_id: account.page_id,
      page_access_token: account.page_access_token,
      instagram_account_id: account.instagram_business_account.id,
      // Sent so the backend can exchange it for a long-lived Page token; the one
      // resolved in the browser expires within the hour.
      user_access_token: userAccessToken,
      redirect_uri: redirectUri
    }

    await instagramService.connect(connectData)

    const status = await instagramService.getStatus()
    setConnectionStatus(status)
    setManualMode(false)
    setManualToken("")
    setFetchedAccounts([])
    setSelectedAccount("")
    notifyChannelConnectionChanged({ channel: 'instagram', connected: true })
  }, [ensureChannelsTabInUrl])

  const fetchAccounts = React.useCallback(async (token: string): Promise<ResolvedInstagramAccount[]> => {
    try {
      setLoading(true)
      setError(null)

      // 1. Get user's pages
      const pagesResponse = await fetch(
        buildMetaGraphUrl(
          `me/accounts?access_token=${token}&fields=id,name,access_token`
        )
      )
      const pagesData = await pagesResponse.json()

      if (pagesData.error) {
        throw new Error(pagesData.error.message)
      }

      const validAccounts: ResolvedInstagramAccount[] = []

      // 2. Resolve linked Instagram accounts for each page.
      for (const page of (pagesData.data || []) as MetaPageAccount[]) {
        if (!page.access_token) {
          log.warn('Skipping Facebook page without Page access token while resolving Instagram account', {
            pageId: page.id,
            pageName: page.name,
          })
          continue
        }

        const instagramAccountId = await fetchPageInstagramAccountId(page.id, page.access_token)

        if (instagramAccountId) {
          validAccounts.push({
            page_id: page.id,
            page_name: page.name,
            page_access_token: page.access_token,
            instagram_business_account: { id: instagramAccountId }
          })
        }
      }

      if (validAccounts.length === 0) {
        setError(translateOrFallback(
          'settings.instagram.no_accounts_found',
          'No Instagram Business accounts found linked to your Facebook Pages.'
        ))
      }

      setFetchedAccounts(validAccounts)
      setSelectedAccount(validAccounts.length === 1 ? validAccounts[0].instagram_business_account.id : "")
      return validAccounts
    } catch (err) {
      console.error('Error fetching accounts:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch accounts')
      return []
    } finally {
      setLoading(false)
    }
  }, [translateOrFallback])

  React.useEffect(() => {
    const initialize = async () => {
      try {
        setInitialLoading(true)

        const oauthRedirectResult = consumeOAuthRedirectParams()
        if (oauthRedirectResult.errorMessage) {
          setError(oauthRedirectResult.errorMessage)
        }

        const token = getConversationsAuthToken()
        if (!token) {
          setConnectionStatus({ connected: false })
          if (!oauthRedirectResult.errorMessage) {
            setError(null)
          }
          return
        }

        const status = await instagramService.getStatus()
        setConnectionStatus(status)

        if (status.connected) {
          setError(null)
          return
        }

        if (oauthRedirectResult.code) {
          await connectWithInstagramLogin(oauthRedirectResult.code)
          return
        }

        if (oauthRedirectResult.accessToken) {
          const accounts = await fetchAccounts(oauthRedirectResult.accessToken)
          if (accounts.length === 1) {
            await connectAccount(accounts[0], oauthRedirectResult.accessToken)
          } else if (accounts.length > 1) {
            setManualToken(oauthRedirectResult.accessToken)
            setManualMode(true)
          }
          return
        }

        if (!oauthRedirectResult.errorMessage) {
          setError(null)
        }
      } catch (err) {
        console.error('Error loading Instagram status:', err)
        setError(err instanceof InstagramServiceError ? err.message : translateOrFallback(
          'settings.instagram.error_loading',
          'Error loading Instagram connection status'
        ))
        setConnectionStatus({ connected: false })
      } finally {
        setInitialLoading(false)
      }
    }

    initialize()
  }, [connectAccount, connectWithInstagramLogin, consumeOAuthRedirectParams, fetchAccounts, getTranslation, translateOrFallback])

  React.useEffect(() => {
    let cancelled = false

    if (facebookOAuthService.isInitialized()) {
      setFacebookSdkReady(true)
      setFacebookSdkError(null)
      return () => {
        cancelled = true
      }
    }

    facebookOAuthService.initialize()
      .then(() => {
        if (cancelled) {
          return
        }
        setFacebookSdkReady(true)
        setFacebookSdkError(null)
      })
      .catch((sdkError) => {
        if (cancelled) {
          return
        }
        setFacebookSdkReady(false)
        setFacebookSdkError(
          sdkError instanceof FacebookOAuthError
            ? sdkError.message
            : 'Facebook login could not be loaded. Please refresh and try again.'
        )
      })

    return () => {
      cancelled = true
    }
  }, [])

  const handleConnect = async () => {
    if (manualMode && selectedAccount) {
      // Manual flow: connect with selected account
      const account = fetchedAccounts.find(a => a.instagram_business_account.id === selectedAccount)
      if (!account) return

      try {
        await connectAccount(account, manualToken || undefined)
      } catch (pageError) {
        console.error('Error connecting:', pageError)
        setError(translateOrFallback('settings.instagram.error_connecting', 'Error connecting to Instagram'))
      }
      return
    }

    // Preferred path: authorise straight on Instagram. It uses the
    // instagram_business_* permissions, which are reviewed separately from the
    // Page-based flow, so an app can be approved here and blocked there.
    if (INSTAGRAM_APP_ID) {
      const redirectUri = instagramRedirectUri()
      try {
        setLoading(true)
        setError(null)
        const code = await instagramOAuthService.login(redirectUri)
        await connectWithInstagramLogin(code)
      } catch (igError) {
        if (igError instanceof InstagramOAuthError) {
          if (igError.cancelled) {
            setError(null)
          } else if (igError.popupBlocked) {
            // Nothing was opened, so falling back to a full-page redirect still
            // completes the same flow.
            window.location.assign(buildInstagramAuthorizeUrl(redirectUri))
            return
          } else {
            setError(igError.message)
          }
        } else {
          setError(
            igError instanceof InstagramServiceError
              ? igError.message
              : translateOrFallback('settings.instagram.error_connecting', 'Failed to connect to Instagram')
          )
        }
      } finally {
        setLoading(false)
      }
      return
    }

    if (!facebookSdkReady) {
      setError(facebookSdkError || 'Facebook login is still loading. Please try again in a moment.')
      return
    }

    try {
      setLoading(true)
      setError(null)

      ensureChannelsTabInUrl()

      // 1. Get Access Token via SDK
      log.debug('Starting Facebook login...')
      // Instagram can use the standard SDK token flow even when no Business Login
      // configuration ID is present for this environment.
      const accessToken = await facebookOAuthService.login(
        INSTAGRAM_CONFIG_ID || undefined,
        'token',
        INSTAGRAM_LOGIN_SCOPES
      )

      if (!accessToken) {
        throw new Error('No access token received')
      }

      // 2. Use the token to fetch accounts (reusing the manual flow logic)
      const accounts = await fetchAccounts(accessToken)

      if (accounts.length === 1) {
        await connectAccount(accounts[0], accessToken)
      } else if (accounts.length > 1) {
        setManualToken(accessToken)
        setManualMode(true)
      }

      // 3. If multiple accounts are available, the UI will show the account selector.

    } catch (err) {
      console.error('Error connecting to Instagram:', err)
      if (err instanceof FacebookOAuthError) {
        const normalizedMessage = err.message.toLowerCase()
        if (normalizedMessage.includes('cancelled') || normalizedMessage.includes('canceled')) {
          setError(null)
        } else {
          setError(err.message)
        }
      } else {
        setError(
          err instanceof InstagramServiceError
            ? err.message
            : translateOrFallback('settings.instagram.error_connecting', 'Failed to connect to Instagram')
        )
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDisconnect = async () => {
    try {
      setLoading(true)
      setError(null)

      await instagramService.disconnect()
      setConnectionStatus({ connected: false })
      notifyChannelConnectionChanged({ channel: 'instagram', connected: false })
    } catch (err) {
      console.error('Error disconnecting from Instagram:', err)
      setError(err instanceof InstagramServiceError ? err.message : translateOrFallback(
        'settings.instagram.error_disconnecting',
        'Error disconnecting Instagram'
      ))
    } finally {
      setLoading(false)
    }
  }

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">{getTranslation('settings.instagram.loading')}</span>
      </div>
    )
  }

  const isConnected = connectionStatus?.connected === true
  // The Instagram Login flow is a plain redirect, so it must not be gated on the
  // Facebook SDK finishing (or ever loading at all).
  const waitingForFacebookSdk = !INSTAGRAM_APP_ID && !facebookSdkReady && !facebookSdkError
  const displayedError = error ?? (INSTAGRAM_APP_ID ? null : facebookSdkError)

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2 flex-shrink-0 relative z-10">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${isConnected
            ? 'bg-success/10 text-success'
            : 'bg-muted text-muted-foreground'
            }`}>
            {isConnected ? (
              <>
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">{getTranslation('settings.instagram.connected_status')}</span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">{getTranslation('settings.instagram.disconnected_status')}</span>
              </>
            )}
          </div>
        </div>

        {isConnected ? (
          <Button
            variant="outline"
            size="default"
            onClick={handleDisconnect}
            disabled={loading}
            className="gap-2 shrink-0 w-full sm:w-auto relative z-0"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{getTranslation('settings.instagram.loading')}</span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4" />
                <span>{getTranslation('settings.instagram.disconnect_button')}</span>
              </>
            )}
          </Button>
        ) : (
          <div className="flex gap-2">
            {!manualMode && (
              <Button
                variant="default"
                size="default"
                onClick={handleConnect}
                disabled={loading || waitingForFacebookSdk}
                className="gap-2 shrink-0 w-full sm:w-auto relative z-0"
              >
                {loading || waitingForFacebookSdk ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{getTranslation('settings.instagram.loading')}</span>
                  </>
                ) : (
                  <>
                    <Instagram className="h-4 w-4" />
                    <span>{getTranslation('settings.instagram.connect_button')}</span>
                  </>
                )}
              </Button>
            )}
            <Button
              variant="ghost"
              size="xs"
              onClick={() => setManualMode(!manualMode)}
            >
              {manualMode ? "Cancel Manual" : "Trouble connecting?"}
            </Button>
          </div>
        )}
      </div>

      {manualMode && !isConnected && (
        <div className="p-4 border rounded-md bg-muted/30 space-y-4">
          <div className="space-y-2">
            <Label>Meta Access Token</Label>
            <div className="flex gap-2">
              <Input
                value={manualToken}
                onChange={(e) => setManualToken(e.target.value)}
                className="flex-1"
                placeholder="Paste your token here..."
              />
              <Button onClick={() => fetchAccounts(manualToken)} disabled={!manualToken || loading} size="sm">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Fetch Accounts"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Use the token provided by the developer.</p>
          </div>

          {fetchedAccounts.length > 0 && (
            <div className="space-y-2">
              <Label>Select Instagram Account</Label>
              <div className="grid gap-2">
                {fetchedAccounts.map((account) => (
                  <div
                    key={account.instagram_business_account.id}
                    className={`p-3 border rounded-md cursor-pointer flex items-center justify-between hover:bg-accent ${selectedAccount === account.instagram_business_account.id ? 'border-primary bg-accent' : ''}`}
                    onClick={() => setSelectedAccount(account.instagram_business_account.id)}
                  >
                    <div>
                      <p className="font-medium text-sm">{account.page_name}</p>
                      <p className="text-xs text-muted-foreground">IG ID: {account.instagram_business_account.id}</p>
                    </div>
                    {selectedAccount === account.instagram_business_account.id && <CheckCircle2 className="h-4 w-4 text-primary" />}
                  </div>
                ))}
              </div>
              <Button onClick={handleConnect} disabled={!selectedAccount || loading} className="w-full mt-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect Selected Account"}
              </Button>
            </div>
          )}
        </div>
      )}

      {displayedError && (
        <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="h-4 w-4" />
            <span>{displayedError}</span>
          </div>
        </div>
      )}

      {isConnected && connectionStatus && (
        <>
          <Separator />
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{getTranslation('settings.instagram.connection_info')}</h3>

            {(connectionStatus.account_id || connectionStatus.metadata?.instagram_account_id) && (
              <div>
                <Label className="text-xs text-muted-foreground">
                  {getTranslation('settings.instagram.account_id')}
                </Label>
                <p className="text-sm font-mono text-muted-foreground mt-1">
                  {connectionStatus.account_id || connectionStatus.metadata?.instagram_account_id}
                </p>
              </div>
            )}

            {connectionStatus.metadata?.instagram_username && (
              <div>
                <Label className="text-xs text-muted-foreground">
                  {getTranslation('settings.instagram.username')}
                </Label>
                <p className="text-sm mt-1">@{connectionStatus.metadata.instagram_username}</p>
              </div>
            )}
          </div>
        </>
      )}

      {!isConnected && !loading && !manualMode && (
        <div className="p-4 rounded-md bg-muted/50 border border-dashed">
          <p className="text-sm text-muted-foreground text-center">
            {getTranslation('settings.instagram.description')}
          </p>
        </div>
      )}
    </div>
  )
}
